/* -----------------Icons--------------- */
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useMemo, useState } from "react";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { ConfirmDialog } from "../ConfirmDialog";
import CustomChip from "../CustomChip";
import DonutChart from "../DonutChart";
import { OverlineText } from "../OverlineText";
import { Skeleton } from "../Skeleton";

export type SubSkillItem = {
	label: string;
	icon: IconType;
	description: string;
	maxPoints: number;
	scoreText: string;
	/** True while the criterion has no resolved score yet (still streaming). */
	pending?: boolean;
	chipProps: {
		backgroundColor: string;
		color: string;
	};
};
type Props = {
	score?: number;
	totalScore: number;
	subskillItems: SubSkillItem[];
	overallScoreLabel: string;
	/** Whether the assessment is still streaming; drives pending-tile shimmer. */
	streaming?: boolean;
};

const ScoreItem = ({
	label,
	score,
	icon,
	backgroundColor,
	color,
	description,
	pending,
}: {
	label: string;
	score: number | string;
	icon: IconType;
	backgroundColor: string;
	color: string;
	description: string | null;
	pending?: boolean;
}) => {
	const theme = useAppTheme();
	const [showInfoDialog, setShowInfoDialog] = useState(false);

	const openInfoDialog = () => {
		setShowInfoDialog(true);
	};

	return (
		<>
			<Pressable
				style={{
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
				}}
				onPress={openInfoDialog}
			>
				<Box
					flexDirection="row"
					alignItems="center"
					gap={16}
					flex={1}
					minWidth={0}
				>
					<Icon name={icon} size={18} color={theme.colors.text} />
					<Text
						variant="bodyLarge"
						numberOfLines={1}
						ellipsizeMode="tail"
						style={{ flexShrink: 1 }}
					>
						{label}
					</Text>
				</Box>
				{pending ? (
					<Skeleton variant="rect" width={52} height={28} />
				) : (
					<CustomChip
						style={{
							backgroundColor,
							flexShrink: 0,
							// Mirrors ScoreChip's default minWidth (55); Paper's Chip root
							// flips flexDirection by platform, so both axes need centering.
							minWidth: 55,
							justifyContent: "center",
							alignItems: "center",
						}}
						textStyle={{ color, textAlign: "center" }}
						onPress={openInfoDialog}
					>
						{score}
					</CustomChip>
				)}
			</Pressable>
			<ConfirmDialog
				dismissCallback={() => setShowInfoDialog(false)}
				body={description ?? ""}
				icon="information-outline"
				title={label}
				visible={showInfoDialog}
				confirmCta="Close"
				confirmCallback={() => setShowInfoDialog(false)}
			/>
		</>
	);
};

const AiPostAnswerHeader = (props: Props) => {
	const { score, totalScore, subskillItems, overallScoreLabel, streaming } =
		props;

	const subskillsStats = useMemo(() => {
		return subskillItems.map(
			({ label, icon, description, scoreText, chipProps, pending }) => {
				const chipColor = chipProps;

				return (
					<ScoreItem
						key={label}
						label={label}
						score={scoreText}
						icon={icon as IconType}
						backgroundColor={chipColor.backgroundColor}
						color={chipColor.color}
						description={description}
						pending={Boolean(pending && streaming)}
					/>
				);
			},
		);
	}, [subskillItems, streaming]);

	return (
		<Box gap={16} marginTop={16}>
			<Box alignItems="center" justifyContent="center">
				<DonutChart
					skeleton={score === undefined}
					distribution={[score ?? 0, totalScore - (score ?? 0)]}
					innerRadius={52}
					variant="progress"
					radius={64}
					score={score ? (score / totalScore) * 100 : undefined}
				>
					<Box alignItems="center" justifyContent="center">
						<Text variant="titleLarge">
							{score === undefined
								? `-- / ${totalScore}`
								: `${Math.round(score)} / ${totalScore}`}
						</Text>
						<OverlineText>{overallScoreLabel}</OverlineText>
					</Box>
				</DonutChart>
			</Box>
			{subskillsStats}
		</Box>
	);
};

export default AiPostAnswerHeader;
